import React, { useState, useEffect } from 'react';
import { useUnsavedChangesSource } from './appUpdate';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  Divider,
  CircularProgress,
  Alert,
  Button,
  Chip,
  MenuItem,
  TextField,
} from '@mui/material';
import {
  Notifications,
  Email,
  Save,
} from '@mui/icons-material';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';

interface ChannelPrefs {
  [key: string]: boolean;
}

interface NotificationPrefs {
  inApp: ChannelPrefs;
  email: ChannelPrefs;
  /** Catalog change recap delivery (flag `change_recap`): live view only, or a weekly email too. */
  changeRecap?: 'live' | 'weekly_digest';
}

const PREF_LABELS: Record<string, { label: string; description: string }> = {
  newProducts: { label: 'New Products', description: 'Products added to your selected brands' },
  priceChanges: { label: 'Price Changes', description: 'Significant price updates on products you follow' },
  mapViolations: { label: 'MAP Violations', description: 'Minimum advertised price violations detected' },
  syncCompleted: { label: 'Sync Completed', description: 'Integration sync jobs that finish successfully' },
  syncFailed: { label: 'Sync Failed', description: 'Sync jobs that fail, and Shopify listings waiting on a match' },
  newProductsAvailable: { label: 'New Products Available', description: 'Newly ingested products matching your brands' },
  systemUpdates: { label: 'System Updates', description: 'Platform announcements and account changes' },
  productAnnouncements: {
    label: 'Product Announcements (RSS)',
    description: 'New press/RSS product signals waiting for manufacturer PDP scrape (admin)',
  },
  storeSignups: {
    label: 'New Store Signups',
    description: 'New accounts that start signup, and stores waiting to be verified (admin)',
  },
  weeklyDigest: { label: 'Weekly Digest', description: 'Weekly summary of activity and changes' },
};

const IN_APP_KEYS = [
  'newProducts',
  'priceChanges',
  'mapViolations',
  'syncCompleted',
  'syncFailed',
  'newProductsAvailable',
  'systemUpdates',
  'productAnnouncements',
  'storeSignups',
];
const EMAIL_KEYS = [
  'newProducts',
  'priceChanges',
  'mapViolations',
  'syncCompleted',
  'syncFailed',
  'newProductsAvailable',
  'systemUpdates',
  'productAnnouncements',
  'storeSignups',
  'weeklyDigest',
];

const DEFAULT_PREFS: NotificationPrefs = {
  inApp: {
    newProducts: true,
    priceChanges: true,
    mapViolations: true,
    syncCompleted: true,
    syncFailed: true,
    newProductsAvailable: true,
    systemUpdates: true,
    productAnnouncements: true,
    storeSignups: true,
  },
  email: {
    newProducts: false,
    priceChanges: true,
    mapViolations: true,
    syncCompleted: false,
    syncFailed: true,
    newProductsAvailable: true,
    systemUpdates: true,
    productAnnouncements: true,
    storeSignups: true,
    weeklyDigest: false,
  },
};

export const NotificationPreferencesCard: React.FC = () => {
  const [prefs, setPrefs] = useState<NotificationPrefs>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const { enabled: changeRecapEnabled } = useOrgFeatureFlag('change_recap');

  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await makeApiCall('/notification-preferences', { headers });
        if (res.ok) {
          const data = await res.json();
          if (data.preferences) {
            setPrefs({
              inApp: { ...DEFAULT_PREFS.inApp, ...(data.preferences.inApp || {}) },
              email: { ...DEFAULT_PREFS.email, ...(data.preferences.email || {}) },
              changeRecap: data.preferences.changeRecap === 'weekly_digest' ? 'weekly_digest' : 'live',
            });
          }
        }
      } catch {
        // Use defaults
      } finally {
        setLoading(false);
      }
    };
    fetchPrefs();
  }, []);

  const handleToggle = (channel: 'inApp' | 'email', key: string) => {
    setPrefs(prev => ({
      ...prev,
      [channel]: { ...prev[channel], [key]: !prev[channel][key] },
    }));
    setDirty(true);
    setSaved(false);
  };

  const handleChangeRecap = (value: 'live' | 'weekly_digest') => {
    setPrefs(prev => ({
      ...prev,
      changeRecap: value,
      // The digest goes out through the Weekly Digest email bucket, so choosing it turns that on.
      email: value === 'weekly_digest' ? { ...prev.email, weeklyDigest: true } : prev.email,
    }));
    setDirty(true);
    setSaved(false);
  };

  const handleSave = async (): Promise<boolean> => {
    setSaving(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/notification-preferences', {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: prefs }),
      });
      if (!res.ok) throw new Error('Failed to save preferences');
      setDirty(false);
      setSaved(true);
      return true;
    } catch (err: any) {
      setError(err.message || 'Failed to save');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Lets the app-update prompt wait for, save or drop these edits.
  useUnsavedChangesSource({
    dirty,
    save: async () => {
      if (!(await handleSave())) throw new Error('save failed');
    },
    discard: () => setDirty(false),
  });

  if (loading) {
    return (
      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </CardContent>
      </Card>
    );
  }

  const renderRow = (key: string, channel: 'inApp' | 'email') => {
    const info = PREF_LABELS[key];
    if (!info) return null;
    return (
      <Box
        key={`${channel}-${key}`}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, px: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{info.label}</Typography>
          <Typography variant="caption" color="text.secondary">{info.description}</Typography>
        </Box>
        <Switch
          size="small"
          checked={!!prefs[channel][key]}
          onChange={() => handleToggle(channel, key)}
        />
      </Box>
    );
  };

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Notifications sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Notification Preferences</Typography>
          </Box>
          {dirty && (
            <Button
              size="small"
              variant="contained"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
              onClick={handleSave}
              disabled={saving}
            >
              Save
            </Button>
          )}
          {saved && !dirty && <Chip label="Saved" size="small" color="success" />}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>{error}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Notifications sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5, color: 'text.secondary' }}>
                In-App Notifications
              </Typography>
            </Box>
            <Divider sx={{ mb: 1 }} />
            {IN_APP_KEYS.map(key => renderRow(key, 'inApp'))}
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Email sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5, color: 'text.secondary' }}>
                Email Notifications
              </Typography>
            </Box>
            <Divider sx={{ mb: 1 }} />
            {EMAIL_KEYS.map(key => renderRow(key, 'email'))}
            {changeRecapEnabled && (
              <TextField
                select
                size="small"
                fullWidth
                label="What changed recap"
                helperText="New brands, new products and price changes on what you sync. Weekly digest emails you on Mondays."
                value={prefs.changeRecap ?? 'live'}
                onChange={(e) => handleChangeRecap(e.target.value as 'live' | 'weekly_digest')}
                sx={{ mt: 2 }}
              >
                <MenuItem value="live">Live view only</MenuItem>
                <MenuItem value="weekly_digest">Weekly digest email</MenuItem>
              </TextField>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
